import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useUserStore } from './user'

const STORAGE_KEY = 'travick.selectedServerId'

function readStored(): number | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (raw === null) return null
    const parsed = Number.parseInt(raw, 10)
    return Number.isNaN(parsed) ? null : parsed
  } catch {
    return null
  }
}

/**
 * Which favourite server the user is currently looking at.
 *
 * This is client-side state on purpose: the backend has no notion of a
 * "current server" for a user — it only stores the favourites list. If that
 * ever becomes a server-side preference, this store is the single place to
 * change.
 */
export const useServerSelectionStore = defineStore('serverSelection', () => {
  const selectedId = ref<number | null>(readStored())
  const user = useUserStore()

  /** Resolves the stored id against the current favourites. */
  const selected = computed(() => {
    // A concluded server is never "current": it moved to the archive, so it
    // must not be resolvable here even if it is still technically a favourite.
    const favorites = user.favoriteServers.filter((server) => !server.closed)
    if (favorites.length === 0) return null

    const match = favorites.find((server) => server.id === selectedId.value)
    // Stored id no longer among the favourites (or never set): show the first.
    return match ?? favorites[0] ?? null
  })

  function select(serverId: number | null): void {
    selectedId.value = serverId
    try {
      if (serverId === null) window.localStorage.removeItem(STORAGE_KEY)
      else window.localStorage.setItem(STORAGE_KEY, String(serverId))
    } catch {
      // Non-fatal: the choice just won't be remembered.
    }
  }

  return { selectedId, selected, select }
})
